import { useCallback, useMemo } from 'react';
import { Params } from 'react-router-dom';
import utils, { getHasElementsInArray } from 'utils';
import useQueryParams from './useQueryParams';
import {
  EMPTY_ARRAY_STRINGIFIED,
  FIRST_PAGE,
  PAGE_PARAM_KEYWORD,
  SORT_PARAM_KEYWORD,
} from 'data/global';

export default function useQueryFilter<T extends Params>(
  pageParam: string | string[] = PAGE_PARAM_KEYWORD,
  sortParam: string | string[] = SORT_PARAM_KEYWORD,
) {
  const {
    params,
    setQueryParam,
    removeQueryParam,
    removeSeveralQueryParams,
    setParamsAndNavigate,
  } = useQueryParams<T>();

  const activeFilter = useMemo(() => {
    const excludeKeys = [pageParam, sortParam].flat();

    return Object.fromEntries(
      Object.entries(params).filter(([key]) => !excludeKeys.includes(key)),
    );
  }, [params, pageParam, sortParam]);

  const hasActiveFilter = useMemo(
    () => getHasElementsInArray(Object.values(activeFilter)),
    [activeFilter],
  );

  const applyQueryFilter = useCallback(
    (key: keyof T, value: string) => {
      if (!value || !value?.length || value === EMPTY_ARRAY_STRINGIFIED)
        removeQueryParam(key);
      else setQueryParam(key, value.toString());
    },
    [removeQueryParam, setQueryParam],
  );

  const onFiltersApply = useCallback(
    (filterKey: string, values: string | string[]) => {
      const queryValues =
        typeof values === 'string' ? values : utils.mapArrayToParams(values);

      applyQueryFilter(filterKey, queryValues);
      applyQueryFilter('page', FIRST_PAGE);
    },
    [applyQueryFilter],
  );

  const onFiltersApplyAndNavigate = useCallback(
    (pathname: string, filterKey: string, values: string | string[]) => {
      const queryValues =
        typeof values === 'string' ? values : utils.mapArrayToParams(values);

      const params = {
        page: FIRST_PAGE,
        [filterKey]: queryValues,
      } as Record<keyof T, string>;

      setParamsAndNavigate(pathname, params);
    },
    [setParamsAndNavigate],
  );

  const resetFilters = useCallback(
    (fields: string[]) => {
      removeSeveralQueryParams(fields);
      setQueryParam('page', FIRST_PAGE);
    },
    [removeSeveralQueryParams, setQueryParam],
  );

  return {
    params,
    activeFilter,
    hasActiveFilter,
    onFiltersApply,
    applyQueryFilter,
    resetFilters,
    onFiltersApplyAndNavigate,
  };
}
